import { Table, TableCell, TableHead, TableHeader, TableRow, TableWrap } from '@/components/ui/Table';
import {
  AdTypeCell,
  CampaignNameCell,
  DateCell,
  DismissButton,
  EmptyTableMessage,
  formatPercent,
  formatRatio,
  SpActionButton,
} from '@/components/recommendations/ruleTables/ruleTableShared';
import { formatRecommendationCurrency } from '@/api/recommendationFormat';
import type { RuleAsinRow } from '@/types/ruleRecommendations';

export function ChokedAsinsTable({
  profileId,
  rows,
  onIncreaseBudget,
  onDismiss,
}: {
  profileId: string;
  rows: RuleAsinRow[];
  onIncreaseBudget: (row: RuleAsinRow) => void;
  onDismiss: (row: RuleAsinRow) => void;
}) {
  if (!rows.length) {
    return <EmptyTableMessage message="No ASINs match these rules for the selected range." />;
  }

  return (
    <TableWrap className="max-h-[28rem] overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow className="divide-y-0 hover:bg-transparent">
            <TableHead>ASIN</TableHead>
            <TableHead>Campaign</TableHead>
            <TableHead>Ad type</TableHead>
            <TableHead>Start date</TableHead>
            <TableHead>End date</TableHead>
            <TableHead className="text-right">Ad sales</TableHead>
            <TableHead className="text-right">CVR</TableHead>
            <TableHead className="text-right">ROAS</TableHead>
            <TableHead className="text-right">Ad spend</TableHead>
            <TableHead className="text-right">Units</TableHead>
            <TableHead className="text-right">Rating</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <tbody>
          {rows.map((row) => (
            <TableRow key={row.asin}>
              <TableCell>
                <div className="min-w-[12rem]">
                  <p className="font-medium text-foreground">{row.productName}</p>
                  <p className="font-mono text-xs text-foreground-muted">{row.asin}</p>
                </div>
              </TableCell>
              <TableCell>
                {row.campaignId ? (
                  <CampaignNameCell
                    profileId={profileId}
                    campaignId={row.campaignId}
                    campaignName={row.campaignName}
                    adProduct={row.adProduct}
                    campaignType={row.campaignType}
                  />
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>
                <AdTypeCell adProduct={row.adProduct} campaignType={row.campaignType} />
              </TableCell>
              <TableCell>
                <DateCell value={row.startDate} empty="—" />
              </TableCell>
              <TableCell>
                <DateCell value={row.endDate} />
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatRecommendationCurrency(row.sales30d)}
              </TableCell>
              <TableCell className="text-right font-mono">{formatPercent(row.cvr)}</TableCell>
              <TableCell className="text-right font-mono">{formatRatio(row.roas)}</TableCell>
              <TableCell className="text-right font-mono">
                {formatRecommendationCurrency(row.cost)}
              </TableCell>
              <TableCell className="text-right font-mono">{row.unitsSold}</TableCell>
              <TableCell className="text-right font-mono">
                {row.rating == null ? '—' : formatRatio(row.rating, 1)}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  <SpActionButton
                    adProduct={row.adProduct}
                    campaignType={row.campaignType}
                    onClick={() => onIncreaseBudget(row)}
                    disabled={!row.campaignId}
                  >
                    Increase budget
                  </SpActionButton>
                  <DismissButton onClick={() => onDismiss(row)} />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}
